import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useAuth } from './hooks/useAuth';
import { ProtectedRoute } from './components/ProtectedRoute';
import { StudentDashboard } from './pages/StudentDashboard';
import { InstructorDashboard } from './pages/InstructorDashboard';
import { AdminDashboard } from './pages/AdminDashboard';
import { ClassDetailsPage } from './pages/ClassDetailsPage';
import { EnvironmentsPage } from './pages/EnvironmentsPage';
import { EnvironmentEditorPage } from './pages/EnvironmentEditorPage';

// Check if we're in dev mode (bypasses Cognito)
const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';

function App() {
  return (
    <Router>
      {isDevMode ? (
        // Dev mode: Skip Cognito authentication
        <DashboardLayout signOut={() => console.log('Sign out (dev mode)')} />
      ) : (
        // Production mode: Use Cognito
        <Authenticator
          components={{
            Header() {
              return (
                <div className="flex flex-col items-center pt-6 pb-2">
                  <img src="/si_logo_lock.svg" alt="Penlabs" style={{ width: 200, height: 135 }} />
                </div>
              );
            },
          }}
          hideSignUp={false}
          variation="default"
          formFields={{
              signUp: {
                given_name: {
                  label: 'First Name',
                  placeholder: 'Enter your first name',
                  isRequired: true,
                  order: 1,
                },
                family_name: {
                  label: 'Last Name',
                  placeholder: 'Enter your last name',
                  isRequired: true,
                  order: 2,
                },
                email: {
                  label: 'Email',
                  placeholder: 'Enter your email',
                  isRequired: true,
                  order: 3,
                },
                password: {
                  label: 'Password',
                  placeholder: 'Enter your password',
                  isRequired: true,
                  order: 4,
                },
                confirm_password: {
                  label: 'Confirm Password',
                  placeholder: 'Re-enter your password',
                  isRequired: true,
                  order: 5,
                },
              },
            }}
        >
          {({ signOut }) => <DashboardLayout signOut={signOut} />}
        </Authenticator>
      )}
    </Router>
  );
}

function DashboardLayout({ signOut }: { signOut?: () => void }) {
  const { loading, hasRole, getDisplayName, getRoleDisplayName } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
              <img src="/si_lock_icon.svg" alt="Penlabs" className="h-8 w-auto" />
              <h1 className="text-2xl font-bold">Penlabs</h1>
            </div>
          <div className="flex items-center gap-4">
            <div className="text-sm">
              <div className="font-medium">{getDisplayName()}</div>
              <div className="text-muted-foreground text-xs">{getRoleDisplayName()}</div>
            </div>
            <button
              onClick={signOut}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b bg-card">
        <div className="container mx-auto px-4">
          <div className="flex gap-1">
            {/* Everyone sees My Classes */}
            <NavLink
              to="/labs"
              className={({ isActive }) =>
                `px-4 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`
              }
            >
              My Classes
            </NavLink>

            {/* Instructors and admins see Manage Classes */}
            {hasRole('instructor') && (
              <NavLink
                to="/classes"
                className={({ isActive }) =>
                  `px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`
                }
              >
                Manage Classes
              </NavLink>
            )}

            {/* Admins only see User Management and Environments */}
            {hasRole('admin') && (
              <NavLink
                to="/admin"
                end
                className={({ isActive }) =>
                  `px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`
                }
              >
                User Management
              </NavLink>
            )}
            {hasRole('admin') && (
              <NavLink
                to="/admin/environments"
                className={({ isActive }) =>
                  `px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`
                }
              >
                Environments
              </NavLink>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 flex-1">
        <Routes>
          {/* Default route redirects to labs */}
          <Route path="/" element={<Navigate to="/labs" replace />} />

          {/* Student Dashboard - Everyone can access */}
          <Route
            path="/labs"
            element={
              <ProtectedRoute requiredRole="student">
                <StudentDashboard />
              </ProtectedRoute>
            }
          />

          {/* Instructor Dashboard - Instructors and admins */}
          <Route
            path="/classes"
            element={
              <ProtectedRoute requiredRole="instructor">
                <InstructorDashboard />
              </ProtectedRoute>
            }
          />

          {/* Class Details Page - Instructors and admins */}
          <Route
            path="/classes/:className"
            element={
              <ProtectedRoute requiredRole="instructor">
                <ClassDetailsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Dashboard - Admins only */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Admin: Environment list */}
          <Route
            path="/admin/environments"
            element={
              <ProtectedRoute requiredRole="admin">
                <EnvironmentsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin: New environment */}
          <Route
            path="/admin/environments/new"
            element={
              <ProtectedRoute requiredRole="admin">
                <EnvironmentEditorPage />
              </ProtectedRoute>
            }
          />

          {/* Admin: Edit environment */}
          <Route
            path="/admin/environments/:id/edit"
            element={
              <ProtectedRoute requiredRole="admin">
                <EnvironmentEditorPage />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to labs */}
          <Route path="*" element={<Navigate to="/labs" replace />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="border-t bg-card py-3">
        <div className="container mx-auto px-4 text-center text-xs text-muted-foreground">
          © Secure Ideas, LLC
        </div>
      </footer>
    </div>
  );
}

export default App;
